import { Inject, Injectable } from '@angular/core';
import EXIF from 'exif-js';
import { checkBrowserAutoRotate, IMAGE_FILE_FORMATS, SentryLoggerService } from '@cakeiteasy/web-utils';
import { HttpClient, HttpRequest } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { MOTIVE_MAX_IMAGE_SIZE_PX } from '../lib/constants/common.constants';

export interface ILoadedImage {
  self: HTMLImageElement;
  name?: string;
  src: string;
  width: number;
  height: number;
  aspectRatio: number;
  isWidthBigger: () => boolean;
}

@Injectable()
export class ImageCropperExtraService {
  private _defaults: {
    fillColor: string;
    maxEdgeSize: number;
    format: string;
    quality: number;
    aspectRatio: number;
  };

  constructor(
    private _logger: SentryLoggerService,
    private http: HttpClient,
    @Inject('AppConfig') private _config: any,
  ) {
    this._defaults = {
      fillColor: null,
      maxEdgeSize: 1000,
      format: 'image/jpeg',
      quality: 1,
      aspectRatio: 1.5,
    };
  }

  public convertHeic(file: File): Promise<any> {
    const headers = { 'Content-Disposition': `form-data; filename="${file.name}"` };
    const url = `${this._config.api.extra.host}${this._config.api.extra.convert_heic}/`;
    const formData: FormData = new FormData();
    formData.append('file', file, file.name);
    let req = new HttpRequest('POST', url, formData);
    req = req.clone({
      setHeaders: headers,
      responseType: 'blob',
    });

    return lastValueFrom(this.http.request(req));
  }

  loadImage(src: string, refImage?: HTMLImageElement, name?: string): Promise<ILoadedImage> {
    return new Promise((res, rej) => {
      const img = refImage || new Image();

      if (src.toLowerCase().indexOf('http') === 0) {
        img.crossOrigin = 'anonymous';
      }

      img.onload = () => {
        const height = img.naturalHeight ? img.naturalHeight : img.height;
        const width = img.naturalWidth ? img.naturalWidth : img.width;

        res({
          self: img,
          name,
          src,
          width,
          height,
          aspectRatio: width / height,
          isWidthBigger: () => width > height,
        });

        img.onload = null;
        img.onerror = null;
      };

      img.onerror = (err) => {
        console.error('loading image error', err);
        rej(img);

        img.onload = null;
        img.onerror = null;
      };

      img.src = src;
    });
  }

  getCanvas({ fillColor = this._defaults.fillColor, width, height }): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    canvas.width = width;
    canvas.height = height;

    if (fillColor) {
      context.fillStyle = fillColor;
    }

    context.fillRect(0, 0, canvas.width, canvas.height);

    return canvas;
  }

  fitAspectRatio(
    img: HTMLImageElement,
    aspectRatio: number,
    format: IMAGE_FILE_FORMATS = IMAGE_FILE_FORMATS.JPG,
    keepBgTransparent: boolean,
  ) {
    const imgHeight = img.naturalHeight;
    const imgWidth = img.naturalWidth;
    let leftOffset = 0;
    let topOffset = 0;
    let canvasWidth = imgWidth;
    let canvasHeight = imgHeight;

    if (aspectRatio > imgWidth / imgHeight) {
      canvasWidth = imgHeight * aspectRatio;
      leftOffset = (canvasWidth - imgWidth) / 2;
    } else {
      canvasHeight = imgWidth / aspectRatio;
      topOffset = (canvasHeight - imgHeight) / 2;
    }

    const canvas = this.getCanvas({
      fillColor: this._getFillStylesBasedOnFormat(format, keepBgTransparent),
      width: canvasWidth,
      height: canvasHeight,
    });

    const context = canvas.getContext('2d');

    context.drawImage(img, 0, 0, imgWidth, imgHeight, leftOffset, topOffset, imgWidth, imgHeight);

    return canvas.toDataURL(format, 1);
  }

  readAsDataURL(file): Promise<any> {
    return this.readAs('readAsDataURL', file).then((response: string) => {
      return this.loadImage(response, null, file.name);
    });
  }

  readAs(readFunction: string, file): Promise<string> {
    const reader = this._getReader();

    return new Promise<string>((res, rej) => {
      reader.onloadend = () => {
        res(reader.result as string);
      };

      reader.onerror = () => {
        rej(reader.result);
      };

      reader[readFunction](file);
    });
  }

  prepareImageForCropping(
    file: File,
    format: IMAGE_FILE_FORMATS,
    keepBgTransparent: boolean,
    maxImageSize = MOTIVE_MAX_IMAGE_SIZE_PX,
  ): Promise<string> {
    let orientation = 1;
    let isSupportAutoOrientation = true;

    return this.readAs('readAsDataURL', file).then((src) => {
      return this._readExifOrientation({ src })
        .then((_orientation: any) => {
          orientation = _orientation;
        })
        .catch(() => {
          console.log('exif is not available');
        })
        .then(checkBrowserAutoRotate)
        .then((_isSupportAutoOrientation: any) => {
          isSupportAutoOrientation = _isSupportAutoOrientation;
        })
        .catch(() => {
          console.log('check browser support autorotation not available');
        })
        .then(() =>
          this._rotateAndResize(src, format, keepBgTransparent, orientation, isSupportAutoOrientation, maxImageSize),
        );
    });
  }

  public checkIfImageIsEmpty(imgUrl: string, logContext: string, uploadedUrl?: string) {
    try {
      return new Promise((res, rej) => {
        const image = new Image();
        image.src = imgUrl;
        image.onload = () => {
          const w = image.naturalWidth;
          const h = image.naturalHeight;

          const c = this.getCanvas({ fillColor: '#000000', width: w, height: h });
          const ctx = c.getContext('2d');
          ctx.fillStyle = 'black';
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(image, 0, 0, w, h);

          const imageData = ctx.getImageData(0, 0, w, h);
          let whitePixels = 0;
          let blackPixels = 0;
          let coloredPixels = 0;
          for (let i = 0; i < imageData.data.length; i += 4) {
            const r = imageData.data[i];
            const g = imageData.data[i + 1];
            const b = imageData.data[i + 2];
            if (r === b && r === g && r === 0) {
              blackPixels++;
            } else if (r === b && r === g && r === 255) {
              whitePixels++;
            } else {
              coloredPixels++;
            }
          }

          const pixelsCount = imageData.data.length / 4;
          const whitePixelsRatio = (whitePixels / pixelsCount) * 100;
          const blackPixelsRatio = (blackPixels / pixelsCount) * 100;
          const coloredPixelsRatio = (coloredPixels / pixelsCount) * 100;
          const logData = {
            naturalWidth: w,
            naturalHeight: h,

            blackPixels,
            whitePixels,
            coloredPixels,
            blackPixelsRatio: blackPixelsRatio + '%',
            whitePixelsRatio: whitePixelsRatio + '%',
            coloredPixelsRatio: coloredPixelsRatio + '%',

            pixelsCount,
            uploadedUrl,
          };

          if (blackPixelsRatio > 95 || whitePixelsRatio > 95) {
            this._logger.captureException(new Error(`TOO Empty image DETECTED: ${JSON.stringify(logData)}`));
          }
        };
      });
    } catch (ex) {
      this._logger.captureException(ex);
    }
  }

  private _getReader() {
    return new FileReader();
  }

  private _changeImageOrientation(
    image: HTMLImageElement,
    format: IMAGE_FILE_FORMATS,
    keepBgTransparent: boolean,
    currentOrientation: number,
    isSupportAutoOrientation: boolean,
    maxImageSize = MOTIVE_MAX_IMAGE_SIZE_PX,
  ) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    let ratio;
    let width, height;

    width = image.naturalWidth;
    height = image.naturalHeight;

    if (width > height && width > maxImageSize) {
      ratio = width / maxImageSize;
      width = maxImageSize;
      height /= ratio;
    } else if (height > width && height > maxImageSize) {
      ratio = height / maxImageSize;
      height = maxImageSize;
      width /= ratio;
    }

    canvas.width = width;
    canvas.height = height;

    if (!isSupportAutoOrientation) {
      // change height and width for 90 degrees rotation
      switch (currentOrientation) {
        case 5:
        case 6:
        case 7:
        case 8:
          const t = canvas.width;
          canvas.width = canvas.height;
          canvas.height = t;
          break;
      }
    }

    context.fillStyle = this._getFillStylesBasedOnFormat(format, keepBgTransparent);

    context.fillRect(0, 0, width, height);
    context.translate(canvas.width / 2, canvas.height / 2);

    if (!isSupportAutoOrientation) {
      switch (currentOrientation) {
        case 2:
          // Horizontal flip
          context.scale(-1, 1);
          break;
        case 3:
          // 180° Rotate CCW
          context.rotate((180 * Math.PI) / 180);
          break;
        case 4:
          // Horizontal flip + 180° Rotate CCW
          context.rotate((180 * Math.PI) / 180);
          context.scale(-1, 1);
          break;
        case 5:
          // Horizontal flip + 90° Rotate CW
          context.rotate((90 * Math.PI) / 180);
          context.scale(-1, 1);
          break;
        case 6:
          // 90° Rotate CW
          context.rotate((90 * Math.PI) / 180);
          break;
        case 7:
          // Horizontal flip + 90° Rotate CCW
          context.rotate((-90 * Math.PI) / 180);
          context.scale(-1, 1);
          break;
        case 8:
          // 90° Rotate CCW
          context.rotate((-90 * Math.PI) / 180);
          break;
      }
    }

    context.drawImage(image, -width / 2, -height / 2, width, height);
    context.restore();

    return canvas.toDataURL(format, 1);
  }

  private _rotateAndResize(
    src: string,
    format: IMAGE_FILE_FORMATS,
    keepBgTransparent: boolean,
    orientation: number,
    isSupportAutoOrientation,
    maxImageSize: number,
  ): Promise<string> {
    return new Promise<string>((res, rej) => {
      const rotatedImage = new Image();
      rotatedImage.onload = () => {
        res(
          this._changeImageOrientation(
            rotatedImage,
            format,
            keepBgTransparent,
            orientation,
            isSupportAutoOrientation,
            maxImageSize,
          ),
        );
      };

      rotatedImage.onerror = (err) => {
        rej(err);
      };

      rotatedImage.src = src;
    });
  }

  private _readExifOrientation(img: { src: string }): Promise<number> {
    return new Promise((res, rej) => {
      try {
        // TODO CHECK IF IT's CORRECT LOGIC HERE we pass obj instea of string
        //@ts-ignore
        EXIF.getData(img, function () {
          return res(EXIF.getTag(this, 'Orientation'));
        });
      } catch (err) {
        rej(err);
      }
    });
  }

  private _getFillStylesBasedOnFormat(format: IMAGE_FILE_FORMATS, keepBgTransparent: boolean) {
    return keepBgTransparent && format === IMAGE_FILE_FORMATS.PNG ? 'transparent' : '#ffffff';
  }
}
